import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * STITCH-2 shared button primitive over the existing .ac-btn token classes
 * (DESIGN DIRECTION law (e)): primary = filled teal, secondary = outline,
 * ghost = borderless text. All variants carry hover / focus-visible / active
 * states from dashboard.css; focus ring is the global 02 sec 7 rule.
 *
 * V7-P4A evolutions (additive; component.ac-button stays the ONE button
 * owner): the 'danger' variant is the design-language 03 P12 DESTRUCTIVE
 * treatment (danger text + danger border on the secondary shape -- never a
 * filled red button on operational surfaces), and the component forwards
 * its ref so dialog focus management (ConfirmDialog) can target real
 * buttons.
 *
 * CLASS-MERGE LAW (SHARED-PRIMITIVES-CONFORMANCE-V1; audit #603 TOP FIVE
 * item 5): a caller-passed className APPENDS to the canonical class list,
 * never displaces it -- .ac-btn and its variant classes always reach the
 * DOM. Before this law, {...rest} was spread after className, so any
 * caller-supplied className silently dropped the canonical classes.
 */
export type AcButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface AcButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: AcButtonVariant;
  readonly size?: 'sm' | 'md';
  readonly children: ReactNode;
}

export function acButtonClass(
  variant: AcButtonVariant = 'secondary',
  size: 'sm' | 'md' = 'md',
): string {
  const classes = ['ac-btn', `ac-btn--${variant}`];
  if (size === 'sm') classes.push('ac-btn--sm');
  return classes.join(' ');
}

const AcButton = forwardRef<HTMLButtonElement, AcButtonProps>(
  function AcButton(
    { variant = 'secondary', size = 'md', children, type, className, ...rest },
    ref,
  ) {
    const canonical = acButtonClass(variant, size);
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={className ? `${canonical} ${className}` : canonical}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

export default AcButton;
